import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Application, CreateApplicationDto, UpdateApplicationDto } from '../models/application.model';

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  private http = inject(HttpClient);

  /**
   * Get all applications by PagerDuty Service ID
   * @param pagerDutyServiceId The PagerDuty Service ID
   * @returns Observable of HttpResponse containing array of Applications
   */
  getApplicationsByServiceId(pagerDutyServiceId: string): Observable<HttpResponse<Application[]>> {
    const endpoint = `${environment.baseUrl}/${environment.serviceConfigUrl}/${pagerDutyServiceId}/applications`;
    return this.http.get<Application[]>(endpoint, { observe: 'response', responseType: 'json' });
  }

  /**
   * Get a single application by ID
   * @param pagerDutyServiceId The PagerDuty Service ID
   * @param applicationId The Application ID
   * @returns Observable of HttpResponse containing the Application
   */
  getApplicationById(pagerDutyServiceId: string, applicationId: string): Observable<HttpResponse<Application>> {
    const endpoint = `${environment.baseUrl}/${environment.serviceConfigUrl}/${pagerDutyServiceId}/applications/${applicationId}`;
    return this.http.get<Application>(endpoint, { observe: 'response', responseType: 'json' });
  }

  /**
   * Create a new application
   * POST - Request body: Application schema (without applicationId and featureId)
   * @param pagerDutyServiceId The PagerDuty Service ID
   * @param applicationDto Application data including features array
   * @returns Observable of HttpResponse containing the created Application (with generated IDs)
   */
  createApplication(pagerDutyServiceId: string, applicationDto: CreateApplicationDto): Observable<HttpResponse<Application>> {
    const endpoint = `${environment.baseUrl}/${environment.serviceConfigUrl}/${pagerDutyServiceId}/applications`;
    return this.http.post<Application>(endpoint, applicationDto, { observe: 'response', responseType: 'json' });
  }

  /**
   * Update an existing application
   * PATCH - Request body: CoreApplication schema (no features, no applicationId)
   * Note: PATCH only updates application-level fields, not features
   * @param pagerDutyServiceId The PagerDuty Service ID
   * @param applicationId The Application ID
   * @param applicationDto Application data (only name, email, distributions - NO features)
   * @returns Observable of HttpResponse containing the updated Application (with all fields including features)
   */
  updateApplication(pagerDutyServiceId: string, applicationId: string, applicationDto: UpdateApplicationDto): Observable<HttpResponse<Application>> {
    const endpoint = `${environment.baseUrl}/${environment.serviceConfigUrl}/${pagerDutyServiceId}/applications/${applicationId}`;
    return this.http.patch<Application>(endpoint, applicationDto, { observe: 'response', responseType: 'json' });
  }

  /**
   * Delete an application
   * @param pagerDutyServiceId The PagerDuty Service ID
   * @param applicationId The Application ID
   * @returns Observable of HttpResponse
   */
  deleteApplication(pagerDutyServiceId: string, applicationId: string): Observable<HttpResponse<void>> {
    const endpoint = `${environment.baseUrl}/${environment.serviceConfigUrl}/${pagerDutyServiceId}/applications/${applicationId}`;
    return this.http.delete<void>(endpoint, { observe: 'response' });
  }
}

